import { Canvas as FabricCanvas, util as fabricNativeUtils } from 'fabric';
import { useState, useEffect, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import getSpread from 'editor/src/store/design/selector/getSpread';
import getSpreadBackgroundImage from 'editor/src/store/design/selector/getSpreadBackgroundImage';
import getSpreadForegroundImage from 'editor/src/store/design/selector/getSpreadForegroundImage';
import { ContentAddress, Coords, Dimensions } from 'editor/src/store/design/types';
import getDefaultZoom from 'editor/src/store/editorModules/panAndZoom/selector/getDefaultZoom';
import { useSelector, useStore } from 'editor/src/store/hooks';

import { captureMessage } from 'editor/src/logger';

import { useBottomBarHeight } from './EditorAreaControls/BottomControls/BottomBarHeightProvider';
import { DEFAULT_TRANSFORM } from './Gestures';
import useGetCenter from './Gestures/useGetCenter';
import useMinZoom from './Gestures/useMinZoom';
import { CanvasRotation, VIEWPORT_CHANGED_EVENT } from './types';
import { POINT_0_0 } from './useCanvasRotation';
import getBestFitZoom from './utils/getBestFitZoom';
import { MAX_ZOOM } from './utils/zoomUtils';

// A zoom of 0 (the canvas is no taller than the bottom bar and its margin,
// e.g. while a phone keyboard is open) makes the viewport singular: its centre
// and every pointer position become NaN, and so does any element moved then.
export const isUsableZoom = (zoom: number) => Number.isFinite(zoom) && zoom > 0;

const isFinitePoint = (point: { x: number; y: number }) => Number.isFinite(point.x) && Number.isFinite(point.y);

function useSetupCanvasDisplay(
  fabricCanvas: FabricCanvas | undefined,
  canvasDim: Dimensions,
  spreadKey: string,
  spreadIndex: number,
  spreadCoords: Coords,
  focusedContentAddress: ContentAddress | undefined,
  mm2px: (x: number) => number,
  utilsSetup: boolean,
  spreadWidth: number,
  spreadHeight: number,
  autoZoom: boolean,
) {
  const previousMobileMenuOpen = useRef(false);
  const store = useStore();
  const { defaultZoom, background, foreground } = useSelector((state) => {
    const spread = getSpread(state, spreadIndex);
    return {
      defaultZoom: autoZoom ? undefined : getDefaultZoom(state),
      background: getSpreadBackgroundImage(state, spread?.name || ''),
      foreground: getSpreadForegroundImage(state, spread?.name || ''),
    };
  }, shallowEqual);

  const [canvasRotation, setCanvasRotation] = useState<CanvasRotation>({
    angleDeg: 0,
    angleRad: 0,
    canvasCenter: POINT_0_0,
  });
  const { bottomBarHeight } = useBottomBarHeight();

  const { minZoom, fitZoom } = useMinZoom(canvasDim, spreadIndex, spreadWidth, spreadHeight, mm2px);
  const { getCenter } = useGetCenter(
    fabricCanvas,
    canvasDim,
    spreadIndex,
    spreadCoords,
    spreadWidth,
    spreadHeight,
    minZoom,
    mm2px,
  );

  const focusedContent = useMemo(() => {
    const spread = getSpread(store.getState(), spreadIndex);
    const focusedPage = focusedContentAddress && spread?.pages[focusedContentAddress.pageIndex];
    return focusedContentAddress && focusedPage?.groups.content?.[focusedContentAddress.contentIndex];
  }, [focusedContentAddress]);

  useEffect(() => {
    const isMobileMenuOpen = store.getState().editor.mobileMenu.isOpen;
    if (!fabricCanvas || !spreadWidth || !spreadHeight || !utilsSetup || isMobileMenuOpen) {
      return;
    }
    const zoom = Math.min(defaultZoom ?? fitZoom, MAX_ZOOM);
    if (!isUsableZoom(zoom)) {
      captureMessage('Skipped canvas setup with unusable zoom', { extra: { zoom, canvasDim } });
      return;
    }

    fabricCanvas.setZoom(zoom);
    const center = getCenter(zoom);
    fabricCanvas.absolutePan(center);

    const angleDeg = focusedContent?.rotate || 0;
    const angleRad = fabricNativeUtils.degreesToRadians(angleDeg);
    const canvasCenter = fabricCanvas.getVpCenter();
    setCanvasRotation((previous) => ({
      angleDeg,
      angleRad,
      canvasCenter: isFinitePoint(canvasCenter) ? canvasCenter : previous.canvasCenter,
    }));

    fabricCanvas.fire(VIEWPORT_CHANGED_EVENT as any);
    fabricCanvas.requestRenderAll();
  }, [getCenter, fitZoom, defaultZoom, !!fabricCanvas, focusedContent, utilsSetup, spreadKey]);

  useEffect(() => {
    (window as any).rotateCanvas = (angleDeg: number) => {
      if (fabricCanvas) {
        const angleRad = fabricNativeUtils.degreesToRadians(angleDeg);
        const canvasCenter = fabricCanvas.getVpCenter();
        setCanvasRotation((previous) => ({
          angleDeg,
          angleRad,
          canvasCenter: isFinitePoint(canvasCenter) ? canvasCenter : previous.canvasCenter,
        }));
      }
    };
  }, [fabricCanvas]);

  useEffect(() => {
    if (!fabricCanvas) {
      return undefined;
    }

    let currentAnimateCancel: { abort: () => void } | undefined;
    let wasLastEffectMobileMenuOpen = false;

    // to avoid unnecessary React work. this should only update fabric canvas
    const cancel = store.subscribe(() => {
      const isMobileMenuOpen = store.getState().editor.mobileMenu.isOpen;
      if (wasLastEffectMobileMenuOpen === isMobileMenuOpen) {
        return;
      }

      // avoid jumping when switching variants on mobile
      let animationDuration = 150;
      if (previousMobileMenuOpen.current && isMobileMenuOpen) {
        animationDuration = 1;
      }

      previousMobileMenuOpen.current = isMobileMenuOpen;
      wasLastEffectMobileMenuOpen = isMobileMenuOpen;
      currentAnimateCancel?.abort();

      const newCanvasDim = isMobileMenuOpen
        ? { width: canvasDim.width, height: Math.round(canvasDim.height * 0.6) }
        : canvasDim;

      const targetZoom = getBestFitZoom(
        newCanvasDim,
        spreadWidth,
        spreadHeight,
        background,
        foreground,
        focusedContent,
        mm2px,
        bottomBarHeight,
      );
      if (!isUsableZoom(targetZoom)) {
        captureMessage('Skipped mobile menu zoom animation with unusable zoom', {
          extra: { targetZoom, canvasDim: newCanvasDim },
        });
        return;
      }

      const currentZoom = fabricCanvas.getZoom();
      const vpt = fabricCanvas.viewportTransform || DEFAULT_TRANSFORM;
      const currentCenter = { x: vpt[4], y: vpt[5] };
      const targetCenter = getCenter(targetZoom, newCanvasDim);

      currentAnimateCancel = fabricNativeUtils.animate({
        startValue: 0,
        endValue: 1,
        duration: animationDuration,
        easing: fabricNativeUtils.ease.easeOutSine,
        onChange: (percentage: number) => {
          const zoomvalue = currentZoom + (targetZoom - currentZoom) * percentage;
          fabricCanvas.setZoom(zoomvalue);
          const vpt = fabricCanvas.viewportTransform || DEFAULT_TRANSFORM;
          vpt[4] = currentCenter.x + (-targetCenter.x - currentCenter.x) * percentage;
          vpt[5] = currentCenter.y + (-targetCenter.y - currentCenter.y) * percentage;
          fabricCanvas.setViewportTransform(vpt);
          fabricCanvas.fire(VIEWPORT_CHANGED_EVENT as any);
          fabricCanvas.requestRenderAll();
        },
        onComplete: () => {
          fabricCanvas.requestRenderAll();
          fabricCanvas.fire(VIEWPORT_CHANGED_EVENT as any);
        },
      });
    });

    return () => cancel();
  }, [getCenter, fitZoom, defaultZoom, !!fabricCanvas, focusedContent, utilsSetup, spreadKey]);

  return canvasRotation;
}

export default useSetupCanvasDisplay;
